import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Check, Sparkles } from "lucide-react";
import { api, trackEvent } from "@/lib/api";
import { useDashboard } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";

const LOADING_STEPS = [
  "Understanding your business…",
  "Mapping your ten business domains…",
  "Calculating digital maturity and AI readiness…",
  "Identifying where transformation creates the most value…",
  "Prioritising what should happen first…",
];

export default function Assessment() {
  const navigate = useNavigate();
  const { data, refresh } = useDashboard();
  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("intro"); // intro | running | submitting | done
  const [loadingStep, setLoadingStep] = useState(0);

  useEffect(() => {
    api.get("/assessment/schema").then(({ data }) => setSchema(data)).catch(() => setSchema(false));
  }, []);

  const allQ = useMemo(() => {
    if (!schema) return {};
    const m = {};
    schema.questions.forEach((q) => (m[q.id] = q));
    Object.values(schema.followups).forEach((q) => (m[q.id] = q));
    return m;
  }, [schema]);

  // Adaptive sequence derived deterministically from current answers.
  const sequence = useMemo(() => {
    if (!schema) return [];
    const seq = [];
    schema.questions.forEach((base) => {
      seq.push(base.id);
      let cur = base.id;
      // walk follow-up chain based on the selected option
      while (answers[cur]) {
        const q = allQ[cur];
        const opt = q?.options.find((o) => o.value === answers[cur]);
        if (opt?.follow_up && allQ[opt.follow_up]) {
          seq.push(opt.follow_up);
          cur = opt.follow_up;
        } else break;
      }
    });
    return seq;
  }, [schema, answers, allQ]);

  if (schema === null) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-72 rounded-2xl" /></div>;
  if (schema === false) return <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">We couldn't load the assessment right now. Please refresh to try again.</div>;

  const isDemo = data?.is_demo;

  // ---------- INTRO ----------
  if (phase === "intro") {
    const domains = Object.entries(schema.domain_labels);
    return (
      <div className="mx-auto max-w-3xl">
        <div className="orbit-eyebrow mb-2">Assessment</div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">An intelligent business conversation</h1>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Not a 50-question form. ORBIT asks about ten areas of your business and adapts as you go — building your
          business map live and turning it into a clear, prioritised plan.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5"><Clock className="h-4 w-4 text-accent" /> {schema.estimated_time}</span>
          <span className="text-muted-foreground">Deterministic &amp; explainable — every score traces to your answers.</span>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {domains.map(([k, label], i) => (
            <div key={k} className="rounded-xl border border-border bg-card px-3 py-2.5 text-center" data-testid={`intro-domain-${k}`}>
              <div className="font-num text-xs text-accent">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-0.5 text-xs font-semibold leading-tight">{label}</div>
            </div>
          ))}
        </div>

        {isDemo ? (
          <div className="mt-8 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
            You're exploring the demonstration company. Its assessment is already complete — view the results from the Dashboard.
            <div className="mt-3"><Button onClick={() => navigate("/app")} className="bg-accent text-accent-foreground hover:bg-accent/90">Go to results</Button></div>
          </div>
        ) : (
          <div className="mt-8">
            <Button size="lg" className="h-12 bg-accent px-7 text-accent-foreground hover:bg-accent/90" onClick={() => { setPhase("running"); trackEvent("ASSESSMENT_STARTED"); }} data-testid="assessment-begin-btn">
              Begin assessment <ArrowRight className="h-4 w-4" />
            </Button>
            {data?.has_data && <p className="mt-3 text-xs text-muted-foreground">Running this again will update your existing results.</p>}
          </div>
        )}
      </div>
    );
  }

  // ---------- SUBMITTING ----------
  if (phase === "submitting") {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center text-center">
        <div className="relative mb-8 h-16 w-16">
          <div className="absolute inset-0 animate-spin rounded-full border-2 border-muted border-t-accent" />
          <Sparkles className="absolute inset-0 m-auto h-6 w-6 text-accent" />
        </div>
        <p className="font-display text-xl font-semibold" data-testid="assessment-loading-msg">{LOADING_STEPS[loadingStep]}</p>
        <div className="mt-6 w-full max-w-xs">
          <Progress value={((loadingStep + 1) / LOADING_STEPS.length) * 100} />
        </div>
      </div>
    );
  }

  // ---------- DONE (transition) ----------
  if (phase === "done") {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center text-center orbit-rise">
        <span className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/15"><Check className="h-8 w-8 text-emerald-600" /></span>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">We've mapped your business.</h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Now let's identify where transformation can create the most value — and what you should do first.
        </p>
        <Button size="lg" className="mt-8 h-12 bg-accent px-7 text-accent-foreground hover:bg-accent/90" onClick={() => navigate("/app")} data-testid="assessment-view-results-btn">
          View my results <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // ---------- RUNNING ----------
  const clampedIndex = Math.min(index, sequence.length - 1);
  const currentId = sequence[clampedIndex];
  const q = allQ[currentId];
  const selected = answers[currentId];
  const answeredCount = sequence.filter((id) => answers[id]).length;
  const pct = Math.round((answeredCount / sequence.length) * 100);
  const isLast = clampedIndex === sequence.length - 1;

  const select = (value) => setAnswers((a) => ({ ...a, [currentId]: value }));

  const submit = async () => {
    setPhase("submitting");
    setLoadingStep(0);
    const timer = setInterval(() => setLoadingStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1)), 900);
    try {
      await api.post("/assessment/submit", { responses: answers });
      await refresh?.();
      setTimeout(() => { clearInterval(timer); setPhase("done"); }, Math.max(0, LOADING_STEPS.length * 900 - 400));
    } catch (e) {
      clearInterval(timer);
      setPhase("running");
      toast.error("We couldn't complete this analysis yet", { description: e.response?.data?.detail || "Please review your answers and try again." });
    }
  };

  const next = () => {
    if (!selected) return;
    if (isLast) submit();
    else setIndex(clampedIndex + 1);
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-semibold uppercase tracking-wider text-accent">{schema.domain_labels[q.domain]}</span>
          <span className="font-num text-muted-foreground">{pct}% complete</span>
        </div>
        <Progress value={pct} data-testid="assessment-progress" />
      </div>

      <div key={currentId} className="orbit-rise rounded-2xl border border-border bg-card p-6 sm:p-8">
        <h2 className="font-display text-xl font-semibold leading-snug sm:text-2xl">{q.text}</h2>
        {q.context && <p className="mt-2 text-sm text-muted-foreground">{q.context}</p>}

        <div className="mt-6 space-y-3" role="radiogroup" aria-label={q.text}>
          {q.options.map((o) => {
            const active = selected === o.value;
            return (
              <button
                key={o.value}
                role="radio"
                aria-checked={active}
                onClick={() => select(o.value)}
                data-testid={`assessment-option-${o.value}`}
                className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? "border-accent bg-accent/5 ring-1 ring-accent" : "border-border bg-background hover:border-accent/40 hover:bg-muted/50"}`}
              >
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${active ? "border-accent bg-accent" : "border-muted-foreground/40"}`}>
                  {active && <Check className="h-3 w-3 text-accent-foreground" />}
                </span>
                <span className="text-sm font-medium">{o.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={() => setIndex(Math.max(0, clampedIndex - 1))} disabled={clampedIndex === 0} data-testid="assessment-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={next} disabled={!selected} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="assessment-next-btn">
          {isLast ? "See my results" : "Continue"} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
